import React, { useEffect, useState } from "react";
import * as Mui from "@mui/material";
import moment from "moment";
import { useSelector } from "react-redux";
import {
  CalendarMonthOutlined,
  CheckRounded,
  CurrencyRupeeOutlined,
  ErrorOutlineRounded,
  FunctionsOutlined,
  PlaceOutlined,
  RemoveRounded,
} from "@mui/icons-material";
import { GlobalSidePanel } from "components/DesignSystem/GlobalSidePanel";
import { RequestInfoBanner } from "components/DesignSystem/RequestInfoBanner";
import { RequestStatusChip } from "components/DesignSystem/RequestStatusChip";
import { RequestDetailsSection } from "components/DesignSystem/RequestDetailsSection";
import { detailAccentPalette } from "components/DesignSystem/RequestDetailField";
import { PrimaryButton } from "components/CustomComponents/DesignSystemButtons";
import { useSavedViewSelector } from "components/CustomComponents/useSavedViewSelector";
import { designColors, designShell, designType, designWeights } from "services/constants/designTokens";
import { lwfRegisterHeader } from "pages/PayRoll/PayRollHeaders";
import { panelBodySx } from "pages/PayRoll/PayInputs/Shared/formUi";
import { EmptyRow, SimpleTable, ToneChip, tableCellSx } from "pages/PayRoll/Salary/Shared/salaryUi";
import { RecordHeaderCard, StatutoryList, initialsOf } from "pages/PayRoll/Shared/statutoryUi";
import { FREQUENCY_OPTIONS, deductInLabel, optionLabel } from "pages/PayRoll/Configuration/StatutoryConfiguration/LWF/lwfConfigData";
import { registerRow } from "pages/PayRoll/Statutory/LWF/lwfEmployeesData";
import { PAYROLL_MONTHS, formatDay, money, num, totalsOf } from "./lwfReportsData";

// LWF Register (LWF4) and the LWF Calculation drawer (LWF4a): every
// employee of the payroll month with their LWF and status. Footer totals
// equal the payroll totals. Held employees are listed but not counted.

const { purple, indigo, green, orange, pink, blue } = detailAccentPalette;
const TONE_COLORS = { green: "#12B76A", amber: "#F0954A", purple: "#6D5DF6", red: "#DC2626", slate: "#6B7280" };

const CalculationPanel = ({ open, row, monthInfo, onClose }) => {
  const [tab, setTab] = useState("month");
  useEffect(() => {
    if (open) setTab("month");
  }, [open, row]);
  if (!row) return null;
  const { version } = row;
  const worked = row.ee != null;

  const ruleFields = version
    ? [
        {
          icon: PlaceOutlined,
          label: "Site",
          value: `${row.site_name} · ${version.name} (from ${formatDay(version.effective_from)})`,
          helper: `From work location on ${moment(`${row.month_key}-01`).endOf("month").format("D MMM YYYY")}`,
          accent: purple,
        },
        ...(version.applicability === "COVERED"
          ? [
              {
                icon: CalendarMonthOutlined,
                label: "Deduction",
                value: `${optionLabel(FREQUENCY_OPTIONS, version.frequency)} · ${deductInLabel(version)} · period ${row.month_key}`,
                accent: indigo,
              },
            ]
          : []),
      ]
    : [{ icon: PlaceOutlined, label: "Site", value: `${row.site_name} — no LWF set-up for ${row.month_label}`, accent: pink }];

  const excluded = version?.excluded_categories || [];
  const coverageFields = [];
  if (version && version.applicability === "COVERED" && row.status !== "NOT_DEDUCTION_MONTH") {
    if (row.rolls) coverageFields.push({ icon: row.rolls.ok ? CheckRounded : RemoveRounded, label: row.rolls.label, value: row.rolls.ok ? "Yes" : "No — not on the rolls", accent: row.rolls.ok ? green : orange });
    if (row.rolls?.ok) {
      if (row.status === "HELD_MISSING_DATA") coverageFields.push({ icon: ErrorOutlineRounded, label: "Category", value: `Missing — site excludes ${excluded.join(", ")}`, accent: pink });
      else if (row.status === "NOT_COVERED_CATEGORY") coverageFields.push({ icon: RemoveRounded, label: "Category", value: `${row.category} — not covered`, accent: orange });
      else coverageFields.push({ icon: CheckRounded, label: "Category", value: `${row.category || "Not set"} — covered`, accent: green });
      if (row.band) {
        coverageFields.push({
          icon: CurrencyRupeeOutlined,
          label: "LWF wage",
          value: `${money(row.lwf_wage)} · band ${row.band.index + 1} (${money(row.band.band.wage_from)} and above)`,
          accent: blue,
        });
      }
    }
  }

  const amountFields = worked
    ? [
        {
          icon: CurrencyRupeeOutlined,
          label: "Employee pays",
          value:
            row.status === "EE_OUTSTANDING"
              ? `${money(row.ee)} · outstanding (not taken from pay)`
              : row.status === "EE_PAID_BY_COMPANY"
                ? `${money(row.ee)} · paid by the company`
                : `${money(row.ee)} · payslip “Labour Welfare Fund”`,
          accent: pink,
        },
        { icon: CurrencyRupeeOutlined, label: "Company pays", value: `${money(row.er)} · company cost`, accent: orange },
        { icon: FunctionsOutlined, label: "Total payable to the Board", value: money(row.ee + row.er), accent: indigo },
      ]
    : [{ icon: FunctionsOutlined, label: "Amounts", value: row.status === "HELD_MISSING_DATA" ? "Held — not worked out until the data is fixed" : "No LWF", accent: indigo }];

  const history = PAYROLL_MONTHS.map((month) => registerRow(row, month.value)).filter(Boolean);

  return (
    <GlobalSidePanel
      open={open}
      onClose={onClose}
      title="LWF Calculation"
      subtitle={`${row.month_label} · from finalised payroll v${monthInfo.version}`}
      bodySx={panelBodySx}
      footer={
        <Mui.Stack direction="row" justifyContent="flex-end" sx={{ width: "100%" }}>
          <PrimaryButton onClick={onClose}>Close</PrimaryButton>
        </Mui.Stack>
      }
    >
      <Mui.Stack spacing={2}>
        <RecordHeaderCard
          avatar={initialsOf(row.name)}
          title={row.name}
          chip={<RequestStatusChip status={row.status_label} colorCode={TONE_COLORS[row.status_tone]} />}
          meta={[row.emp_id, row.site_name, row.category || "no category"].join(" · ")}
          tone={worked ? "teal" : row.status_tone === "red" ? "danger" : "slate"}
        />
        <Mui.Tabs value={tab} onChange={(event, next) => setTab(next)} sx={designShell.panelTabs}>
          <Mui.Tab value="month" label="This Month" />
          <Mui.Tab value="history" label="History" />
        </Mui.Tabs>
        {tab === "month" ? (
          <>
            <RequestDetailsSection title="RULE USED" fields={ruleFields} />
            {coverageFields.length ? <RequestDetailsSection title="COVERAGE" fields={coverageFields} /> : null}
            <RequestDetailsSection title="AMOUNTS" fields={amountFields} />
          </>
        ) : (
          <SimpleTable
            fit
            heads={[
              { label: "Month", width: 90 },
              { label: "Version", width: 90 },
              { label: "Employee (₹)", width: 100 },
              { label: "Employer (₹)", width: 100 },
              { label: "Status", width: 180 },
            ]}
          >
            {history.length ? (
              history.map((entry) => (
                <Mui.TableRow key={entry.month_key}>
                  <Mui.TableCell sx={tableCellSx}>{entry.month_label}</Mui.TableCell>
                  <Mui.TableCell sx={tableCellSx}>{entry.version?.name || "—"}</Mui.TableCell>
                  <Mui.TableCell sx={tableCellSx}>{entry.ee == null ? "—" : num(entry.ee)}</Mui.TableCell>
                  <Mui.TableCell sx={tableCellSx}>{entry.er == null ? "—" : num(entry.er)}</Mui.TableCell>
                  <Mui.TableCell sx={tableCellSx}>
                    <ToneChip label={entry.status_label} tone={entry.status_tone} />
                  </Mui.TableCell>
                </Mui.TableRow>
              ))
            ) : (
              <EmptyRow colSpan={5} text="No finalised payrolls yet." />
            )}
          </SimpleTable>
        )}
      </Mui.Stack>
    </GlobalSidePanel>
  );
};

export const LwfRegisterTab = ({ monthInfo, rows }) => {
  const { domain, userDetails, topbarCompanyName } = useSelector((state) => state.tokenReducer);
  const [viewId, setViewId] = useState(null);
  const { visibleHeaders, titleOverride } = useSavedViewSelector({
    domain,
    userId: userDetails?.id,
    storageKey: "lwf_register_saved_views",
    fullHeaders: lwfRegisterHeader,
  });
  const totals = totalsOf(rows);
  const viewed = rows.find((row) => row.id === viewId);
  const amount = (value) => (value == null ? "—" : num(value));

  const renderCell = (row, key) => {
    switch (key) {
      case "name":
        return (
          <Mui.Stack direction="row" spacing={1} alignItems="center">
            <Mui.Avatar sx={{ width: 28, height: 28, fontSize: 11, fontWeight: 700, backgroundColor: designColors.slate100, color: designColors.slate700 }}>
              {initialsOf(row.name)}
            </Mui.Avatar>
            <Mui.Typography sx={{ ...designType.tableBodyCell, fontWeight: designWeights.medium, whiteSpace: "nowrap" }}>{row.name}</Mui.Typography>
          </Mui.Stack>
        );
      case "lwf_wage":
      case "ee":
      case "er":
      case "total":
        return amount(row[key]);
      case "status_label":
        return <ToneChip label={row.status_label} tone={row.status_tone} />;
      default:
        return row[key] || "—";
    }
  };

  return (
    <>
      <StatutoryList
        title={`LWF Register ${monthInfo.label}`}
        headers={visibleHeaders}
        titleOverride={titleOverride}
        rows={rows}
        renderCell={renderCell}
        onRowClick={(row) => setViewId(row.id)}
        selectedKey={viewId}
        itemLabel="employees"
        searchPlaceholder="Search name or ID"
        metadata={{ "Company Name": topbarCompanyName || "N/A", "Payroll Month": monthInfo.label, "Report Generated On": moment().format("DD/MM/YYYY HH:mm:ss") }}
      />
      <Mui.Box sx={{ ...designShell.sectionCard, mt: 1.5, px: 2, py: 1.25, textAlign: { sm: "right" } }}>
        <Mui.Typography sx={{ ...designType.tableBodyCell, fontWeight: designWeights.semibold }}>
          Totals: employee {money(totals.employee)}
          {totals.outstanding ? ` (${money(totals.outstanding)} outstanding)` : ""} · employer {money(totals.employer)} · {money(totals.total)}
          {totals.held ? ` · ${totals.held} held` : ""}
        </Mui.Typography>
      </Mui.Box>
      <Mui.Box sx={{ mt: 1.5 }}>
        <RequestInfoBanner body="Held — not worked out until missing data is fixed (not counted as zero). Outstanding — employee share due but not taken from pay; it is still part of the payable to the Board. In non-deduction months the register shows “Not a deduction month”." />
      </Mui.Box>
      <CalculationPanel open={Boolean(viewed)} row={viewed} monthInfo={monthInfo} onClose={() => setViewId(null)} />
    </>
  );
};
